import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ModalContainer from "designSystem/components/dialog/ModalContainer";
import ListItem from "designSystem/components/list/ListItem";
import { useLocale } from "designSystem/locale";
import ResizeHandle from "designSystem/components/pane/ResizeHandle";
import { STANDARD_BORDER_STYLE, navListStyle } from "designSystem/styles/style-functions";
import Tooltip from "designSystem/components/tooltip/Tooltip";
import csi from "csi/main";
import { confirmLeavePage } from "csi/router";
import { useStore } from "csi/state/hooks";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { CHANGE_TRACKING, CHANGE_TRACKING_SLIM } from "csi/service/rightPaneService";

const PREFIX = "RightPane";

const classes = {
	item: `${PREFIX}item`,
	list: `${PREFIX}list`,
};

const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	[`& .${classes.item}`]: {
		...navListStyle(theme),
		borderLeftStyle: "solid",
		borderRightStyle: "none",
		height: ICON_WIDTH,
		paddingLeft: 8,
		width: ICON_WIDTH,
	},

	[`& .${classes.list}`]: {
		borderLeft: STANDARD_BORDER_STYLE,
	},
}));

const BORDER_WIDTH = 1;
const ICON_WIDTH = 38;
const CHANGE_LOG_VIEWS = [CHANGE_TRACKING, CHANGE_TRACKING_SLIM];

function isChangeLogView(viewId: string): boolean {
	return CHANGE_LOG_VIEWS.includes(viewId);
}

export const RightPane = observer(() => {
	const { rightPaneStore, routerStore, threeDStore } = useStore();
	const {
		adHocComponent,
		closeSelected,
		dialogComponent,
		dialogProps,
		isFullWidth,
		selected,
		selectedId,
		selectedWidth,
		setSelected,
		showTooltip,
	} = rightPaneStore;

	const tooltipPlacement = isFullWidth ? "right" : "left";
	const components = rightPaneStore.getAvailableComponents(threeDStore?.docNode || routerStore.locationNode);
	const resizable = selected?.config.resizable;
	const minWidth = selected ? (selected.config.viewWidth + (resizable ? 0 : BORDER_WIDTH)) : 0;
	const width = selectedWidth || minWidth;
	const loc = useLocale();

	// component must be capitalized to use in JSX
	const SelectedView = selected ? csi.syncRequire(selected.config.view)?.default : null;
	const DialogComponent = dialogComponent;
	const dialogContainerId = "csi-right-pane-selected";

	const onResizeComponent = action((delta: number, dragging: boolean) => {
		// since width increases to the left, need to subtract delta rather than add; 
		// only allow width to grow to 2 times its minimum size
		const w = Math.min(Math.max(minWidth, width - delta), minWidth * 2);
		rightPaneStore.selectedWidth = w;
		selected.config.setResizeWidth?.(w);
	});

	return (
		<StyledBox
			display="flex"
			height={1}
			position="relative"
		>
			{!adHocComponent && (
				<List className={classes.list}>
					{components.map((component) => {
						const { config, id } = component;
						const { icon, tooltipTitle } = config;
						const ComponentIcon = csi.syncRequire(icon)?.default;
						// we use the same button for Change Log Slim and Detailed views
						const isSelected = (id === selectedId) || (isChangeLogView(id) && isChangeLogView(selectedId));
						const tooltip = tooltipTitle ? loc.resource(tooltipTitle) : id;

						const onClick = async () => {
							if (selected?.config.confirmLeaving?.()) {
								try {
									await confirmLeavePage();
								} catch (ex) {
									return;
								}
							}

							if (isSelected) {
								closeSelected();
							} else {
								setSelected(id);
							}
						};

						return showTooltip && ComponentIcon && (
							<Tooltip
								key={id}
								placement={tooltipPlacement}
								res={id}
								title={tooltip}
							>
								<div>
									<ListItem
										button
										className={classes.item}
										design="nav"
										disableGutters
										onClick={onClick}
										selected={isSelected}
									>
										<ListItemIcon>
											<ComponentIcon color={isSelected ? "primary" : "action"} />
										</ListItemIcon>
									</ListItem>
								</div>
							</Tooltip>
						);
					})}
				</List>
			)}
			{selected && (
				<ModalContainer
					display="flex"
					flexGrow={1}
					width="100%"
				>
					{resizable &&
						<ResizeHandle onResize={onResizeComponent} />
					}
					<Box
						borderLeft={resizable ? null : STANDARD_BORDER_STYLE}
						flexGrow={1}
						height={1}
						id={dialogContainerId}
						width={width}
					>
						<SelectedView />
					</Box>
					{DialogComponent && (
						<DialogComponent
							container={() => document.getElementById(dialogContainerId)}
							dataCsiAutomation="RightPaneDialog"
							{...dialogProps}
						/>
					)}
				</ModalContainer>
			)}
		</StyledBox>
	);
});

export default RightPane;
